import { ApolloError } from '@apollo/client'
import {
  ButtonPrevious,
  CheckButtonTheme,
  DynamicFooter as Footer,
  StepIndicator,
  theme,
} from '@by-miles/bymiles-styles'
import { DataLayer } from '@by-miles/data-layer'
import { emailRegex } from '@by-miles/regex'
import { datadogRum } from '@datadog/browser-rum'
import React, { useContext, useEffect, useRef } from 'react'
import Helmet from 'react-helmet'
import { useHistory, useLocation } from 'react-router-dom'
import shajs from 'sha.js'

import { VideoPlayer } from '../components/ExplainerVideo/VideoPlayer'
import LoadingMask from '../components/LoadingMask'
import { PromoBanner } from '../components/PromoBanner'
import { Header } from '../components/shared'
import { SmartSticky } from '../components/SmartSticky'
import { stickyBannerCookieHeight } from '../CookiesFunctions'
import { QuoteMainFragment } from '../generated/graphql'
import { PaymentType } from '../generated/graphql'
import useChatWidget from '../hooks/useChatWidget'
import { useQuote } from '../hooks/useQuote'
import useStepNavigation from '../hooks/useStepNavigation'
import { NavigationStyles } from '../pages/styles/NavigationStyles'
import { ProviderContext } from '../Provider'
import { explainerVideoPicker } from '../utils/explainerVideoPicker'
import { footerLinks, footerText } from '../utils/FooterLinks'
import { LuckyOrange } from '../utils/luckyOrange'

interface HOCProps {
  gaPageName: string
  pageName?: string
  backButtonLabel?: string
  backButtonUrl?: string
  nextButtonUrl?: string
  hideStickyPricing: boolean
  hideSaveQuote?: boolean
  priceQuoteOnPageLoad?: boolean
  onLogoClick?: Function
  pageTitle: string
  steps?: string[]
  stepIndex?: number
  showPromo?: boolean
}

export interface BasePageProps {
  quote: QuoteMainFragment | undefined | null
  pageName?: string
  error: ApolloError | undefined
}

export const BasePage = <P,>(Component: React.FC<P>): React.FunctionComponent<HOCProps> =>
  function WithBasePage({
    pageName,
    gaPageName,
    backButtonLabel,
    backButtonUrl,
    nextButtonUrl,
    hideStickyPricing = false,
    priceQuoteOnPageLoad = false,
    pageTitle,
    steps,
    stepIndex = 0,
    showPromo,
    ...props
  }): React.FunctionComponentElement<P & BasePageProps> {
    const { state, handleShowModal, simpleStateUpdater, handleCloseModal } = useContext(ProviderContext)
    const quote = useQuote()
    const location = useLocation()
    const history = useHistory()

    const { setupChatWidget, setChatEmail } = useChatWidget()
    const { validateCheckoutNavigation } = useStepNavigation()

    const email = quote?.email

    useEffect(() => {
      simpleStateUpdater([{ state: 'pageName', value: pageName }])
    }, [pageName])

    useEffect(() => {
      setupChatWidget(quote?.details?.proposer?.first_names + ' ' + quote?.details.proposer?.last_names)
      if (email) setChatEmail(email)
    }, [email])

    const isRefundAtRenewal = quote?.details.policy?.paymentType === PaymentType.RefundAtRenewalYearly
    const url = explainerVideoPicker({
      isRefundAtRenewal,
      isTrackerless: state.isTrackerless,
      fixed: state?.quotePricing?.fixedUpfront || '',
      pmRate: quote?.pricing?.perMileRate.toString() || '',
      estimatedMilage: state.quotePricing.estimatedMiles,
      yearlyEstimate: state.quotePricing.projectedAnnual,
      carMake: quote?.details?.vehicle?.make || '',
    })
    const iframeVideoRef = useRef()
    const triggerAutoPlay = () => {
      if (isRefundAtRenewal) return
      const iframe = iframeVideoRef.current as unknown as HTMLIFrameElement
      const playBtn = iframe?.contentWindow?.document.querySelector('label[for="playpause"]') as HTMLElement
      if (!playBtn) return
      if (state.isMobile) {
        const controls = iframe?.contentWindow?.document.querySelector('.animation-controls') as HTMLElement
        controls.style.opacity = '1'
      } else {
        playBtn.click()
      }
    }

    useEffect(() => {
      if (state.currentLocation !== location.pathname) {
        simpleStateUpdater([{ state: 'currentLocation', value: location.pathname }])
        if (state.activeModal && state.activeModal !== 'zero-id-score') {
          simpleStateUpdater([{ state: 'showModal', value: false }])
          document.body.classList.remove('noScroll')
        }
      }
    }, [location.pathname, state.currentLocation, state.activeModal])

    useEffect(() => {
      DataLayer.customEvent({
        eventName: gaPageName,
        section: 'full quote',
        eventType: 'virtual page view',
        customerId: quote?.details?.customerId || '',
        make: quote?.details.vehicle.make || '',
        policyType: quote?.details.policy?.isTrackerless ? 'Trackerless' : 'Tracked',
        paymentJourney: quote?.details.policy?.paymentType || '',
      })
    }, [location.pathname])

    useEffect(() => {
      if (quote) {
        const trackerConfirmed = quote?.details?.site?.trackerConfirmed
        const docsConfirmed = quote?.details?.site?.docsConfirmed

        simpleStateUpdater([
          { state: 'isTrackerless', value: quote?.details?.policy?.isTrackerless },
          { state: 'quoteEmail', value: quote?.details?.proposer?.email },
        ])

        if (trackerConfirmed)
          simpleStateUpdater([{ state: 'trackerConfirmed', value: quote?.details?.site?.trackerConfirmed }])

        if (docsConfirmed) simpleStateUpdater([{ state: 'docsConfirmed', value: quote?.details?.site?.docsConfirmed }])

        if (quote.details.site?.experiments) {
          quote.details.site?.experiments?.forEach(experiment => {
            experiment && LuckyOrange.tag(experiment.experimentId as string, false, true)
            experiment && datadogRum.addRumGlobalContext('experiment', experiment.experimentId as string)
          })
        }
      }

      if (quote?.email) {
        const id = shajs('sha256').update(quote.email.toLowerCase()).digest('hex').slice(0, 10)
        datadogRum.setUser({ id, type: 'customer', quoteId: quote.id, customerId: quote.details?.customerId || '' })
      }
    }, [quote])

    useEffect(() => {
      const cookieYesIcon: HTMLElement | null = document.querySelector('.cky-revisit-bottom-left')
      if (cookieYesIcon) cookieYesIcon.style.bottom = state.showSticky ? stickyBannerCookieHeight() : '10px'
    }, [state.showSticky])

    const previousButtonOnClick = (url: string | undefined) => {
      if (url === 'details/car') simpleStateUpdater([{ state: 'showSticky', value: false }])
      if (!url) {
        return
      } else {
        history.push(url)
      }
    }

    const handleSavedButtonClicked = () => {
      DataLayer.customEvent({
        eventName: 'save quote button',
        section: 'quote',
        eventType: 'form tracking',
      })
      handleShowModal('saveQuoteModal')
    }

    const handleLogoButtonClicked = () => {
      DataLayer.customEvent({
        eventName: 'logo',
        section: 'header',
        eventType: 'button click',
      })
      if (props.onLogoClick) {
        props.onLogoClick()
      }
    }
    const quotePage = gaPageName === '/ga-virtual/checkout/quote'

    return (
      <>
        <Header
          onClick={handleLogoButtonClicked}
          onSaveQuoteClick={handleSavedButtonClicked}
          hideSaveQuote={props.hideSaveQuote || !state?.quoteEmail?.match(emailRegex) || false}
        />
        <Helmet title={pageTitle} />
        {quote?.promo && showPromo && (
          <PromoBanner
            firstName={quote?.promo?.firstName || ''}
            amount={quote?.promo?.amount}
            type={quote?.promo?.type}
          />
        )}
        <NavigationStyles quotePage={quotePage}>
          {backButtonLabel && (
            <div className="back-button-wrapper LoNotSensitive">
              <ButtonPrevious
                label={backButtonLabel}
                onClick={() => previousButtonOnClick(backButtonUrl)}
                tabFocusTrap={state.showModal ? -1 : 0}
                fill={quotePage && theme.primaryColours.white.main}
              />
            </div>
          )}
          {steps && (
            <StepIndicator
              buttonTheme={stepIndex === 0 ? CheckButtonTheme.Light : CheckButtonTheme.Dark}
              preFilledStep={steps[stepIndex]}
              steps={steps}
              validateOnClick={(target: string) => validateCheckoutNavigation(target, stepIndex)}
            />
          )}
        </NavigationStyles>
        {state.pageLoading === 'overlay' && <LoadingMask />}
        <Component {...(props as P)} quote={quote} pageName={pageName} />
        {!hideStickyPricing && (
          <SmartSticky
            targetId="sticky-entry-point"
            payTodayAmount={state.quotePricing.fixedUpfront}
            projectedAnnual={state.quotePricing.projectedAnnualYearly}
            payMonthlyAmount={state.quotePricing.averageMonthly}
            estimatedMileage={state.quotePricing.estimatedMiles}
            loading={state.isFetching}
            onClick={() => nextButtonUrl && validateCheckoutNavigation(nextButtonUrl, stepIndex)}
            buttonError={pageTitle === 'Confirm' ? state.confirmPageErrors.stickyError : false}
          />
        )}

        {state.showModal && state.activeModal === 'play-video' && (
          <VideoPlayer
            handleCloseVideo={() => {
              handleCloseModal('play-video')
              if (state.isMobile) {
                const cookieYesIcon = document.querySelector<HTMLElement>('.cky-revisit-bottom-left')
                if (cookieYesIcon) cookieYesIcon.style.display = 'flex'
              }
            }}
            iframeVideoRef={iframeVideoRef}
            triggerAutoPlay={triggerAutoPlay}
            cookieConsent={false}
            hideDismissIcon={false}
            isMobile={state.isMobile}
            url={url}
          />
        )}

        <Footer
          links={footerLinks}
          text={footerText}
          tabFocusTrap={state.showModal ? -1 : 0}
          hideStickyPricing={hideStickyPricing}
        />
      </>
    )
  }
